import { useMutation, useQueryClient } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';

import {
  type AdminApproval,
  AdminApprovalDecision,
  adminapprovalCreateAdminApprovalReviewBatchMutation,
  inviteListInvitesQueryKey,
} from '@column/api';
import {
  Button,
  DataTable,
  type DataTableColumnDef,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  cn,
} from '@column/ui';

import { MAX_INVITE_SELECTION } from '../_hooks/useInviteApprovalSelection';
import { StatusBadge } from '~/components/Table/StatusBadge';
import { LogoLoading } from '~/elements';
import { getErrorMessage } from '~/lib/api/getErrorMessage';
import { ReviewDialog } from '~/lib/approvals/components/ReviewDialog';
import { invalidateAdminApprovals } from '~/lib/approvals/queries';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

export interface InviteApprovalBatchRequest {
  approvals: AdminApproval[];
  decision: AdminApprovalDecision;
  roleLabels: Record<string, string>;
}

const PAGE_SIZE = 10;

interface InviteReviewResult {
  approval: AdminApproval;
  error?: string;
  retryable?: boolean;
  skipped?: string;
}

const skipReasons: Record<string, string> = {
  not_found: 'This invite no longer exists.',
  not_pending: 'This invite is no longer pending approval.',
  expired: 'This invite has expired.',
  invalid_reviewer: 'You cannot review your own invite.',
  no_permission: 'You no longer have permission to review this invite.',
};

interface Props {
  request: InviteApprovalBatchRequest;
  onClose: () => void;
  onReviewed: (id: string) => void;
}

export const InviteApprovalBatchDialog: React.FC<Props> = ({ request, onClose, onReviewed }) => {
  const queryClient = useQueryClient();
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();
  const [comment, setComment] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [results, setResults] = useState<InviteReviewResult[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const submitting = useRef(false);
  const active = useRef(true);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);
  const mutation = useMutation({ ...adminapprovalCreateAdminApprovalReviewBatchMutation(), retry: false });
  const isApprove = request.decision === AdminApprovalDecision.APPROVED;
  const action = isApprove ? 'Approve' : 'Reject';
  const retryable = results.filter((result) => result.retryable);
  const close = () => {
    if (!submitting.current) onClose();
  };

  const submit = async (approvals: AdminApproval[]) => {
    if (submitting.current || approvals.length === 0) return;
    submitting.current = true;
    const retryIds = new Set(approvals.map((approval) => approval.id));
    setResults((previous) => previous.filter((result) => !retryIds.has(result.approval.id)));
    const { currentPlatform, currentUser, isSandbox } = useSessionStore.getState();
    const platformId = currentPlatform?.id;
    const userId = currentUser?.id;
    const canContinue = () => {
      const session = useSessionStore.getState();
      return (
        active.current &&
        !!platformId &&
        !!userId &&
        session.currentPlatform?.id === platformId &&
        session.currentUser?.id === userId &&
        session.isSandbox === isSandbox
      );
    };
    let nextResults: InviteReviewResult[];
    let failureMessage: string | undefined;
    try {
      if (!canContinue()) throw new Error('Review stopped because the current page or session changed.');
      if (approvals.length > MAX_INVITE_SELECTION) {
        throw new Error(`Select at most ${MAX_INVITE_SELECTION} invites.`);
      }
      const batch = await mutation.mutateAsync({
        body: {
          admin_approval_ids: approvals.map((approval) => approval.id),
          decision: request.decision,
          comment: comment.trim() || undefined,
        },
      });
      const records = new Map(batch.records.map((record) => [record.admin_approval_id, record]));
      nextResults = approvals.map((approval) => {
        const record = records.get(approval.id);
        if (record?.status === 'skipped') {
          return {
            approval,
            skipped: skipReasons[record.skip_reason ?? ''] ?? 'This invite can no longer be reviewed.',
          };
        }
        if (!record) return { approval, error: 'No review outcome was returned for this invite.', retryable: true };
        if (record.status !== request.decision) {
          return { approval, error: `Unexpected review outcome: ${record.status}.` };
        }
        return { approval };
      });
      const failedCount = nextResults.filter((result) => result.error).length;
      if (failedCount > 0) {
        failureMessage = `Failed to review ${failedCount} ${failedCount === 1 ? 'invite' : 'invites'}.`;
      }
    } catch (cause) {
      failureMessage = getErrorMessage(cause, 'Failed to review the selected invites.');
      nextResults = approvals.map((approval) => ({ approval, error: failureMessage, retryable: true }));
    } finally {
      submitting.current = false;
      if (canContinue()) {
        invalidateAdminApprovals(queryClient);
        queryClient.invalidateQueries({ queryKey: inviteListInvitesQueryKey() });
      }
    }
    if (canContinue()) {
      setResults((previous) => [...previous, ...nextResults]);
      setSubmitted(true);
      if (failureMessage) addDangerNotification({ content: failureMessage });
      nextResults.forEach((result) => {
        if (!result.error) onReviewed(result.approval.id);
      });
      const allResults = [...results.filter((result) => !retryIds.has(result.approval.id)), ...nextResults];
      if (allResults.every((result) => !result.error && !result.skipped)) {
        const count = request.approvals.length;
        addSuccessNotification({
          content: `${count} ${count === 1 ? 'invite' : 'invites'} ${isApprove ? 'sent' : 'rejected'}`,
        });
        onClose();
      }
    }
  };

  const resultsById = new Map(results.map((result) => [result.approval.id, result]));
  const columns: DataTableColumnDef<AdminApproval>[] = [
    {
      id: 'email',
      header: 'Email',
      width: 'minmax(min-content, 2fr)',
      cell: ({ row }) => <DataTable.TextCell value={row.request.user_invite?.email} weight="medium" />,
    },
    {
      id: 'role',
      header: 'Role',
      width: 'minmax(min-content, 1fr)',
      cell: ({ row }) => <StatusBadge>{request.roleLabels[row.id]}</StatusBadge>,
    },
    ...(submitted
      ? ([
          {
            id: 'status',
            header: 'Status',
            width: 'minmax(min-content, 1fr)',
            cell: ({ row }) => {
              const result = resultsById.get(row.id);
              if (!result) return null;
              if (result.error || result.skipped) {
                return (
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="flex">
                          <StatusBadge tone={result.error ? 'danger' : 'warning'}>
                            {result.error ? 'Failed' : 'Skipped'}
                          </StatusBadge>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs">{result.error ?? result.skipped}</TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              }
              return <StatusBadge tone="success">{isApprove ? 'Invited' : 'Rejected'}</StatusBadge>;
            },
          },
        ] satisfies DataTableColumnDef<AdminApproval>[])
      : []),
  ];
  const lastPage = Math.max(0, Math.ceil(request.approvals.length / PAGE_SIZE) - 1);

  return (
    <ReviewDialog isOpen onClose={close} showCloseButton={!mutation.isPending}>
      <DialogHeader>
        <DialogTitle>
          {action} {request.approvals.length} {request.approvals.length === 1 ? 'Invite' : 'Invites'}
        </DialogTitle>
        <DialogDescription>
          {isApprove ? 'Invites will be sent to the selected users.' : 'The selected invites will be rejected.'}
        </DialogDescription>
      </DialogHeader>
      <DataTable<AdminApproval>
        columns={columns}
        data={request.approvals.slice(pageIndex * PAGE_SIZE, (pageIndex + 1) * PAGE_SIZE)}
        isLoading={mutation.isPending}
        loadingContent={<LogoLoading />}
        className="min-h-0 [&_[data-slot=data-table-loading]]:min-h-0"
        rowKey={(row) => row.id}
        rowClassName="min-h-11"
        pagination={
          lastPage === 0
            ? undefined
            : {
                pageIndex,
                pageSize: PAGE_SIZE,
                hasPrevious: pageIndex > 0,
                hasNext: pageIndex < lastPage,
                onPrevious: () => setPageIndex((index) => Math.max(0, index - 1)),
                onNext: () => setPageIndex((index) => Math.min(lastPage, index + 1)),
                className: 'justify-end',
              }
        }
      />
      {!submitted && (
        <div className="grid gap-2">
          <Label htmlFor="invite-batch-comment">Comment</Label>
          <Input
            id="invite-batch-comment"
            value={comment}
            placeholder="Add a comment..."
            disabled={mutation.isPending}
            onChange={(event) => setComment(event.target.value.slice(0, 1023))}
          />
        </div>
      )}
      <DialogFooter
        className={cn(
          'flex-row items-center',
          submitted && retryable.length === 0 ? 'justify-end sm:justify-end' : 'justify-between sm:justify-between'
        )}
      >
        {submitted ? (
          <>
            <Button type="button" variant="secondary" size="sm" onClick={close} disabled={mutation.isPending}>
              Done
            </Button>
            {retryable.length > 0 && (
              <Button
                type="button"
                variant={isApprove ? 'primary' : 'danger'}
                size="sm"
                disabled={mutation.isPending}
                onClick={() => submit(retryable.map((result) => result.approval))}
              >
                {mutation.isPending && <Spinner />}
                Retry Failed
              </Button>
            )}
          </>
        ) : (
          <>
            <Button type="button" variant="secondary" size="sm" onClick={close} disabled={mutation.isPending}>
              Cancel
            </Button>
            <Button
              type="button"
              variant={isApprove ? 'primary' : 'danger'}
              size="sm"
              disabled={mutation.isPending}
              onClick={() => submit(request.approvals)}
            >
              {mutation.isPending && <Spinner />}
              {action}
            </Button>
          </>
        )}
      </DialogFooter>
    </ReviewDialog>
  );
};
